// CW Squad management figures: KPIs, filterable daily chart (team or one person), in/out ratios, per-person table.
import { Fragment } from "react";
import { ChevronDown, ChevronRight, MousePointerClick } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { dur, clock } from "../../../components/squad/squadUtils";

const num = (n) => (n || 0).toLocaleString("en-GB");
export const METRICS = [
  { key: "active_s", label: "Active", color: "#34d399" },
  { key: "brb_manual_s", label: "BRB", color: "#fbbf24" },
  { key: "brb_auto_s", label: "Auto away", color: "#fb923c" },
];

export function Kpis({ t }) {
  const items = [
    ["Online time", dur(t.online_s), "text-white"], ["Active time", dur(t.active_s), "text-emerald-300"],
    ["Available", `${t.avail_pct}%`, "text-emerald-200"], ["Sessions", t.sessions, "text-sky-200"],
    ["BRB breaks", `${t.brb_count} · ${dur(t.brb_s)}`, "text-amber-300"], ["Auto away", `${t.auto_count} · ${dur(t.auto_s)}`, "text-orange-300"],
    ["Clicks", num(t.clicks), "text-violet-200"], ["Keystrokes", num(t.keys), "text-violet-200"],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="squad-kpis">
      {items.map(([l, v, c]) => (
        <div key={l} className="rounded-xl border border-white/[0.07] bg-black/20 px-3 py-2.5">
          <div className="text-[9.5px] uppercase tracking-widest font-mono text-slate-500">{l}</div>
          <div className={`text-[17px] font-bold tabular-nums ${c}`} data-testid={`squad-kpi-${l.toLowerCase().replace(/\s+/g, "-")}`}>{v}</div>
        </div>
      ))}
    </div>
  );
}

function monthDays(month) {
  const [y, m] = month.split("-").map(Number);
  const n = new Date(y, m, 0).getDate();
  const now = new Date();
  const last = y === now.getFullYear() && m === now.getMonth() + 1 ? now.getDate() : n;
  return Array.from({ length: last }, (_, i) => `${month}-${String(i + 1).padStart(2, "0")}`);
}

function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-[#0F1420] px-3 py-2 text-[12px]">
      <div className="text-slate-400 mb-1">Day {label}</div>
      {payload.map((p) => <div key={p.dataKey} style={{ color: p.color }}>{p.name}: {dur(p.payload[`raw_${p.dataKey}`])}</div>)}
    </div>
  );
}

export function TeamChart({ daily, people, month, person, setPerson, metrics, setMetrics }) {
  const src = person ? (people.find((p) => p.user_id === person)?.days || []) : daily;
  const by = Object.fromEntries(src.map((d) => [d.date, d]));
  const peak = Math.max(0, ...src.map((d) => METRICS.reduce((a, m) => a + (metrics.includes(m.key) ? d[m.key] || 0 : 0), 0)));
  const scale = peak < 3600 ? 60 : 1;
  const data = monthDays(month).map((date) => {
    const d = by[date] || {};
    return { day: date.slice(8), ...Object.fromEntries(METRICS.flatMap((m) => [[m.key, +(((d[m.key] || 0) / 3600) * scale).toFixed(3)], [`raw_${m.key}`, d[m.key] || 0]])) };
  });
  const any = src.length > 0;
  const toggle = (k) => setMetrics(metrics.includes(k) ? metrics.filter((x) => x !== k) : [...metrics, k]);
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="squad-team-chart">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="w-full mb-1">
          <h2 className="text-base md:text-lg font-semibold text-white">Daily time split</h2>
          <p className="text-[12px] text-slate-400">Hours per day — pick the team or one person, and which times to show.</p>
        </div>
        <select value={person || ""} onChange={(e) => setPerson(e.target.value || null)} data-testid="squad-chart-person"
          className="h-8 px-2 rounded-lg bg-[#0F1A30] border border-white/10 text-[12.5px] text-slate-200">
          <option value="">Whole team</option>
          {people.map((p) => <option key={p.user_id} value={p.user_id}>{p.name}</option>)}
        </select>
        {METRICS.map((m) => (
          <button key={m.key} type="button" onClick={() => toggle(m.key)} data-testid={`squad-chart-metric-${m.key}`}
            className={`h-8 px-3 rounded-full border text-[12px] inline-flex items-center gap-1.5 transition-colors ${metrics.includes(m.key) ? "border-white/25 text-white bg-white/[0.06]" : "border-white/10 text-slate-500"}`}>
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: m.color, opacity: metrics.includes(m.key) ? 1 : 0.3 }} />{m.label}
          </button>
        ))}
      </div>
      {any && metrics.length ? (
        <div className="h-[260px]">
          <ResponsiveContainer>
            <BarChart data={data} barCategoryGap="18%">
              <CartesianGrid stroke="rgba(255,255,255,.05)" vertical={false} />
              <XAxis dataKey="day" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} unit={scale === 60 ? "m" : "h"} allowDecimals={false} />
              <Tooltip content={<ChartTip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
              {METRICS.filter((m) => metrics.includes(m.key)).map((m, i, arr) => (
                <Bar key={m.key} dataKey={m.key} name={m.label} stackId="a" fill={m.color} radius={i === arr.length - 1 ? [4, 4, 0, 0] : 0} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : <div className="py-10 text-center text-slate-500 text-sm" data-testid="squad-chart-empty">{metrics.length ? "No activity for this selection yet." : "Pick at least one time type."}</div>}
    </section>
  );
}

function RatioBar({ t, label, onClick, selected, testid }) {
  const online = t.online_s || 0;
  const parts = METRICS.map((m) => ({ ...m, s: t[m.key] || 0 }));
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`w-full flex items-center gap-3 px-2 py-1.5 rounded-lg text-left transition-colors ${selected ? "bg-white/[0.06]" : "hover:bg-white/[0.03]"}`}>
      <span className="w-[84px] sm:w-[130px] shrink-0 text-[12.5px] text-white truncate">{label}</span>
      <span className="flex-1 min-w-[40px] h-3 rounded-full bg-white/[0.04] overflow-hidden flex">
        {online > 0 && parts.map((p) => <span key={p.key} title={`${p.label} ${dur(p.s)}`} style={{ width: `${(100 * p.s) / online}%`, background: p.color }} />)}
      </span>
      <span className="w-[92px] sm:w-[120px] shrink-0 text-[10.5px] sm:text-[11px] tabular-nums text-slate-400 text-right">
        <span className="text-emerald-300">{t.avail_pct || 0}% in</span> · <span className="text-amber-200">{online ? 100 - (t.avail_pct || 0) : 0}% out</span>
      </span>
    </button>
  );
}

export function RatioPanel({ people, totals, person, setPerson }) {
  const team = { ...totals, brb_manual_s: totals.brb_s, brb_auto_s: totals.auto_s };
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="squad-ratio-panel">
      <h2 className="text-base md:text-lg font-semibold text-white">In vs out</h2>
      <p className="text-[12px] text-slate-400 mb-3">Share of online time spent active, on BRB and auto away. Click a row to focus the chart.</p>
      <RatioBar t={team} label="Team total" selected={!person} onClick={() => setPerson(null)} testid="squad-ratio-team" />
      <div className="my-1.5 border-t border-white/[0.05]" />
      {people.map((p) => (
        <RatioBar key={p.user_id} t={p.totals} label={p.name} selected={person === p.user_id}
          onClick={() => setPerson(person === p.user_id ? null : p.user_id)} testid={`squad-ratio-${p.user_id}`} />
      ))}
    </section>
  );
}

const COLS = ["Staff", "Days", "First seen", "Last seen", "Online", "Active", "Avail.", "Sessions", "BRB (manual)", "Auto away", "Longest", "Clicks", "Keys"];

function Cells({ r, first, last, label }) {
  return (
    <>
      <td className="py-2 pl-2 text-white whitespace-nowrap">{label}</td>
      <td className="tabular-nums">{r.days_worked ?? (r.online_s ? 1 : 0)}</td>
      <td className="tabular-nums">{first}</td><td className="tabular-nums">{last}</td>
      <td className="tabular-nums">{dur(r.online_s)}</td><td className="tabular-nums text-emerald-300">{dur(r.active_s)}</td>
      <td className="tabular-nums">{r.avail_pct}%</td><td className="tabular-nums">{r.sessions}</td>
      <td className="tabular-nums text-amber-200">{r.brb_manual_n} · {dur(r.brb_manual_s)}</td>
      <td className="tabular-nums text-orange-200">{r.brb_auto_n} · {dur(r.brb_auto_s)}</td>
      <td className="tabular-nums">{dur(r.longest_s)}</td>
      <td className="tabular-nums text-violet-200">{num(r.clicks)}</td>
      <td className="tabular-nums text-violet-200">{num(r.keys)}</td>
    </>
  );
}

export function PeopleTable({ people, person, setPerson }) {
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 overflow-x-auto" data-testid="squad-people-table">
      <h2 className="text-base md:text-lg font-semibold text-white mb-3 flex items-center gap-2">Per person <MousePointerClick className="w-4 h-4 text-violet-300" /></h2>
      <table className="w-full text-[12.5px] min-w-[1050px]">
        <thead><tr className="text-left text-[10.5px] uppercase tracking-wider text-slate-500 border-b border-white/[0.06]">
          {COLS.map((c) => <th key={c} className="py-2 pl-2 font-medium">{c}</th>)}
        </tr></thead>
        <tbody>
          {people.map((p) => (
            <Fragment key={p.user_id}>
              <tr className={`border-b border-white/[0.04] cursor-pointer ${person === p.user_id ? "bg-white/[0.05]" : "hover:bg-white/[0.03]"}`}
                onClick={() => setPerson(person === p.user_id ? null : p.user_id)} data-testid={`squad-person-row-${p.user_id}`}>
                <Cells r={p.totals} first={p.totals.avg_first_seen || "—"} last={p.totals.avg_last_seen || "—"}
                  label={<span className="inline-flex items-center gap-1">{person === p.user_id ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}{p.name}</span>} />
              </tr>
              {person === p.user_id && p.days.map((d) => (
                <tr key={d.date} className="bg-black/20 text-slate-300 border-b border-white/[0.03]" data-testid={`squad-person-day-${p.user_id}-${d.date}`}>
                  <Cells r={d} first={clock(d.first_seen)} last={clock(d.last_seen)} label={<span className="pl-4 text-slate-400">{d.date}</span>} />
                </tr>
              ))}
            </Fragment>
          ))}
          {!people.length && <tr><td colSpan={COLS.length} className="py-6 text-center text-slate-500">Nothing recorded this month.</td></tr>}
        </tbody>
      </table>
      <p className="text-[11px] text-slate-500 mt-2">First/last seen on the person row are monthly averages. Click a person for their day-by-day breakdown — the chart and ratios follow your selection. Clicks/keys are counted from today onwards.</p>
    </section>
  );
}
